import React, { useState, useEffect } from "react";
import {Box, Collapsible, Heading, Text} from "grommet";
import Loading from "./Loading";
import Icon from "./Icon";
import { kebabCase, snakeCase } from "../utils/string";
import { useRouter } from "next/router";
import { followHash } from "../utils/hashScroll";
import {
    IconLink,
    IconChevronDown as IconArrowDown
} from '@tabler/icons-react';
import Button from "./Button";

const Section = ({
    title,
    level = 2,
    anchorId,
    // Old anchor ids (from earlier titles) that should still land on this section, so
    // links people copied before a rename keep working
    legacyAnchorIds = [],
    children,
    control,
    headerProps,
    contentProps,
    controlProps,
    titleProps,
    fullWidthControls,
    // Wrap the control under the title only when the two do not fit on one line
    wrapControls=false,
    subheader,
    icon, // streamline icon
    iconProps,
    customIcon,
    isLoading,
    hideBorder=false,
    leftControl,
    titleContainerProps,
    subheaderProps,
    collapsible,
    collapsedByDefault=false,
    hideDeepLink=false,
    ...rest
}) => {
    const [isCollapsed, setIsCollapsed] = useState(collapsedByDefault);
    const [showCopyLink, setShowCopyLink] = useState(false);
    const [isLinkCopied, setIsLinkCopied] = useState(false);
    const autoGeneratedId = (typeof title == 'string') ? kebabCase(title?.toString() || '') : null;
    const router = useRouter();

    useEffect(() => {
        followHash();
    },[]);

    const copyLink = async () => {
        const link = `${window.location.origin}${router.asPath.split("#")[0]}#${anchorId || autoGeneratedId}`;
        await navigator.clipboard.writeText(link);
        setIsLinkCopied(true);
        setTimeout(() => {
            setIsLinkCopied(false); // reset tooltip text
        }, 1000);
    };

    const content = (
        <Box pad="xsmall" {...contentProps}>
            {isLoading ? (<Loading />) : children }
        </Box>
    );

    return (
        <Box
            margin={isCollapsed ?  { top: 'medium' }: { vertical: 'medium' }}
            id={ anchorId || autoGeneratedId}
            round="xsmall"
            flex={false}
            {...rest}
        >
            {legacyAnchorIds.map((legacyId) => <span key={legacyId} id={legacyId} aria-hidden="true" />)}
            <Box
                border={(title || control) && !hideBorder && !isCollapsed ? {side: 'bottom', color: 'border'} : false}
                flex={false}
                {...headerProps}
                fill="horizontal">
                <Box direction="row"
                    data-testid="section-header-row"
                    wrap={wrapControls}
                    onMouseEnter={() => {setShowCopyLink(true)}}
                    onMouseLeave={() => {setShowCopyLink(false); setIsLinkCopied(false);}}
                >
                    {(title || customIcon)&&
                    <Box 
                        justify="start" 
                        direction="row" 
                        gap="xsmall" 
                        align="center" 
                        margin={{bottom: subheader ? 'small' : '0'}} 
                        {...titleContainerProps}>
                        {leftControl}
                        {icon && (
                            // Never shrinks: a title long enough to wrap squeezed the icon on a phone
                            <Box flex={false}>
                                <Icon SelectedIcon={icon} highlight="accent" size="medium" {...iconProps} />
                            </Box>
                        )}
                        {customIcon}
                        <Box direction="column">
                            {title && (
                                <Heading
                                    responsive={false}
                                    align="start"
                                    level={level}
                                    size="medium"
                                    margin={{horizontal: 'xsmall', vertical: subheader ? 'xxsmall' : 'small'}}
                                    {...titleProps}>
                                    <Box direction="row" gap="small" align="center">
                                        {collapsible ? (
                                            <Button
                                                event={{section: `${snakeCase(title)}_section`, element: 'collapse'}}
                                                onClick={() => setIsCollapsed(!isCollapsed)} 
                                                hoverIndicator 
                                                plain>
                                                <Box align="center" direction="row" gap="small">
                                                    <Text size="large">{title}</Text>
                                                    <Box>
                                                        <Icon SelectedIcon={IconArrowDown} size="xxsmall" rotation={isCollapsed ? 90 : 0}/>
                                                    </Box>
                                                </Box>
                                            </Button>
                                        ) : title}
                                        { !hideDeepLink && showCopyLink && autoGeneratedId &&
                                            <Button
                                                event={{section: `${snakeCase(title)}_section`, element: 'copy_section_link'}}
                                                onClick={copyLink}
                                                tip={isLinkCopied ? "Copied!" : "Copy link"}
                                                a11yTitle="Copy link to this section"
                                                plain
                                            >
                                                {({ hover }) => (
                                                    <Box pad="xxsmall" round="xsmall" align="center" justify="center" background={hover ? 'hover' : undefined}>
                                                        <Icon SelectedIcon={IconLink} size="xsmall" color="text-weak" />
                                                    </Box>
                                                )}
                                            </Button>
                                        }
                                    </Box>
                                </Heading>
                            )}
                            {subheader && (<Text {...subheaderProps}>{subheader}</Text>)}
                        </Box>
                    </Box>}
                    {control && !fullWidthControls && (
                        // A zero basis never overflows, so it would never wrap: size it by its content
                        <Box margin={{ bottom: 'xxsmall' }} justify="center" align="end" flex basis={wrapControls ? 'auto' : undefined} {...controlProps}>{control}</Box>
                    )}
                </Box>
                { control && fullWidthControls && !isCollapsed && <Box margin={{ bottom: 'xxsmall' }} justify="center" align="end" flex {...controlProps}>{control}</Box>}
            </Box>
            {/*
                Only wrapped in a Collapsible when it can actually collapse. Grommet's
                Collapsible renders a Box that sizes to its content and forwards no props,
                so inside a height-constrained parent (a Drawer) it refuses to shrink below
                that content: whatever sits under it never gets a bounded height, so
                `overflow: auto` never scrolls and anything shrinkable gets squashed instead.
            */}
            {(children || isLoading) ?
                ((collapsible || collapsedByDefault)
                    ? (<Collapsible open={!isCollapsed}>{content}</Collapsible>)
                    : content):
                (<></>)
            }
        </Box>
    )
};

export default Section;
